(function (root) {
  'use strict';
  if (root.__kmCartVariantSwitcherReady) return;

  function parseProperties(value) {
    if (!value) return {};
    try {
      var parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        // Liquid can serialize line item properties as key/value pairs.
        return Object.fromEntries(parsed.filter(function (entry) {
          return Array.isArray(entry) && entry.length === 2 && typeof entry[0] === 'string';
        }));
      }
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  }

  function route(form, path) {
    var rootUrl = (form && form.dataset.cartRoot) || '/';
    return rootUrl.replace(/\/?$/, '/') + path.replace(/^\//, '');
  }

  async function request(url, body) {
    var response;
    try {
      var options = {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {Accept: 'application/json'}
      };
      if (body) {
        options.method = 'POST';
        options.headers['Content-Type'] = 'application/json';
        options.body = JSON.stringify(body);
      }
      response = await root.fetch(url, options);
    } catch (error) {
      throw new Error('Unable to update this product option. Please try again.');
    }
    var result;
    try { result = await response.json(); }
    catch (error) { throw new Error('Unable to read the cart update response. Please try again.'); }
    if (!response.ok || !result || result.status) {
      throw new Error(result && (result.description || result.message) || 'Unable to update this product option. Please try again.');
    }
    if (body && (!Array.isArray(result.items) || (Array.isArray(body.items) &&
      !body.items.every(function (requested) {
        return result.items.some(function (item) {
          return item && item.key && String(item.variant_id || item.id) === String(requested.id);
        });
      })))) {
      throw new Error('Unable to verify the cart update. Please refresh your cart and try again.');
    }
    return result;
  }

  async function readCart(form) {
    var cart = await request(route(form, 'cart.js'));
    if (!Array.isArray(cart.items)) throw new Error('Unable to read your cart. Please try again.');
    return cart;
  }

  function propertyIdentity(properties) {
    return JSON.stringify(Object.keys(properties || {}).sort().map(function (key) {
      return [key, properties[key]];
    }));
  }

  function sellingPlan(item) {
    return String(item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || '');
  }

  function findLine(cart, reference) {
    var exact = reference.key && cart.items.find(function (item) { return item.key === reference.key; });
    if (exact) return exact;
    var matches = cart.items.filter(function (item) {
      return String(item.variant_id || item.id) === String(reference.variant_id || reference.id)
        && sellingPlan(item) === sellingPlan(reference)
        && propertyIdentity(item.properties) === propertyIdentity(reference.properties);
    });
    if (matches.length !== 1) throw new Error('Unable to identify this cart item. Please refresh your cart and try again.');
    return matches[0];
  }

  async function switchVariant(select) {
    if (!select || select.dataset.kmVariantPending === 'true') return;
    var form = select.closest('form');
    var previous = select.dataset.kmVariantCurrent || select.value;
    var next = select.value;
    if (!next || next === previous || select.dataset.kmVariantRefreshFailed === 'true') return;
    var key = select.dataset.kmCartVariantKey;
    var reference = {key: key, id: Number(previous), properties: parseProperties(select.dataset.kmCartVariantProperties),
      selling_plan: select.dataset.kmCartVariantSellingPlan};
    var errorNode = select.closest('[data-km-cart-line]')?.querySelector('[data-km-cart-variant-error]')
      || select.parentElement?.querySelector('[data-km-cart-variant-error]');
    var nativeCart = form && form.kmCartForm;
    var operation = nativeCart && nativeCart.beginOperation && nativeCart.beginOperation('variant');
    var succeeded = false;
    var changed = false;
    select.dataset.kmVariantPending = 'true';
    select.disabled = true;
    if (errorNode) { errorNode.hidden = true; errorNode.textContent = ''; }
    try {
      var source = findLine(await readCart(form), reference);
      var quantity = Math.max(1, Number(source.quantity));
      var item = {id: Number(next), quantity: quantity, properties: source.properties || {}};
      if (sellingPlan(source)) item.selling_plan = Number(sellingPlan(source));
      var added = await request(route(form, 'cart/add.js'), {items: [item]});
      try {
        // Adding a variant can recalculate discounts and invalidate existing line keys.
        var currentSource = findLine(await readCart(form), source);
        await request(route(form, 'cart/change.js'), {id: currentSource.key, quantity: 0});
      } catch (error) {
        var addedLine = added.items && added.items[0];
        try {
          if (!addedLine || !addedLine.key) throw new Error('Missing added cart line.');
          var currentAdded = findLine(await readCart(form), {key: addedLine.key, id: item.id,
            properties: item.properties, selling_plan: item.selling_plan});
          // Shopify merges matching lines; undo only the quantity added by this switch.
          await request(route(form, 'cart/change.js'), {
            id: currentAdded.key,
            quantity: Math.max(0, Number(currentAdded.quantity) - quantity)
          });
        } catch (rollbackError) {
          root.document.dispatchEvent(new root.CustomEvent('cart:build'));
          throw new Error(error.message + ' Please review your cart before checking out.');
        }
        throw error;
      }
      select.dataset.kmVariantCurrent = next;
      changed = true;
      root.document.dispatchEvent(new root.CustomEvent('cart:build'));
      if (nativeCart && nativeCart.cartBuildPromise) await nativeCart.cartBuildPromise;
      succeeded = true;
    } catch (error) {
      if (!changed) select.value = previous;
      if (errorNode) {
        errorNode.textContent = error.message;
        errorNode.hidden = false;
      }
    } finally {
      if (operation) nativeCart.finishOperation(operation, succeeded);
      delete select.dataset.kmVariantPending;
      var refreshFailed = changed && !succeeded;
      select.dataset.kmVariantRefreshFailed = String(refreshFailed);
      select.disabled = refreshFailed;
      if (refreshFailed && nativeCart && nativeCart.showCartRefreshError) nativeCart.showCartRefreshError('variant');
    }
  }

  function handleChange(event) {
    var select = event.target.closest && event.target.closest('[data-km-cart-variant-select]');
    if (select) switchVariant(select);
  }

  root.document.addEventListener('change', handleChange);
  root.__kmCartVariantSwitcherReady = true;

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {parseProperties: parseProperties, switchVariant: switchVariant};
  }
})(typeof window !== 'undefined' ? window : globalThis);
